import { html, LitElement, TemplateResult } from "lit";
import { customElement, query, queryAssignedNodes } from "lit/decorators.js";

import { style } from "./assets/horizontal-card.generated-style";
import { HORIZONTAL_CARD_EVENT_CLICK } from "./horizontal-card.constants";

/**
 * Horizontal cards contain content.
 *
 * @CustomElement slh-horizontal-card
 *
 * @cssprop --slh-color-neutral-shade-00 - Background color.
 * @cssprop --slh-color-neutral-shade-20 - Icon slot background color.
 * @cssprop --slh-color-neutral-shade-60 - Icon slot icon color.
 * @cssprop --slh-default-font-family - Content font family.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 * slots
 * @slot icon - Left slh-icon holder.
 * @slot content - Content holder.
 * @slot action - Right slh-link holder.
 */
@customElement("slh-horizontal-card")
export default class SlhHorizontalCard extends LitElement {
  @queryAssignedNodes({ slot: "icon" })
  protected iconSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "action" })
  protected actionSlotNodes: Array<HTMLElement> | undefined;

  @query(".content", false)
  private contentElement: HTMLElement | undefined;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("horizontal-card-wrapper");
  }

  static styles = style;

  protected firstUpdated(): void {
    this.setContentClasses();
  }

  render(): TemplateResult {
    return html`
      <div
        tabindex="0"
        class="hotizontal-card-content"
        @click="${this.clickEvent}"
      >
        <slot name="icon" @slotchange="${this.setContentClasses()}"></slot>
        <div class="content">
          <slot name="content"></slot>
        </div>
        <slot name="action" @slotchange="${this.setContentClasses()}"></slot>
      </div>
    `;
  }

  private setContentClasses(): void {
    if (this.iconSlotNodes?.length === 0) {
      this.contentElement?.classList.add("left-radius");
    } else {
      this.contentElement?.classList.remove("left-radius");
    }
    if (this.actionSlotNodes?.length === 0) {
      this.contentElement?.classList.add("right-radius");
      this.contentElement?.classList.add("pe-xl");
    } else {
      this.contentElement?.classList.remove("right-radius");
    }
  }

  /**
   * @fires "click-horizontal-card"
   */
  private clickEvent() {
    const customEvent = new CustomEvent(HORIZONTAL_CARD_EVENT_CLICK, {
      detail: {
        data: this.dataset,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }
}
